import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Clock,
  CreditCard,
  CheckCircle,
  Mail,
  ArrowRight,
  AlertCircle,
  DollarSign,
  Calendar,
  Shield,
} from "lucide-react";
import logger from "../utils/logger";

const PaymentPending = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant, admin, planId } = location.state || {};

  // ✅ السماح بالوصول إلى الصفحة حتى بدون بيانات المستأجر والمسؤول
  // سيتم عرض معلومات افتراضية في حالة عدم وجود بيانات
  React.useEffect(() => {
    if (!tenant || !admin) {
      logger.info("⚠️ No tenant or admin data, showing default page");
    }
  }, [tenant, admin, navigate]);

  // معلومات الخطط
  const planInfo = {
    2: { name: "الأساسية", price: "199 ريال", period: "شهرياً" },
    3: { name: "الاحترافية", price: "399 ريال", period: "شهرياً" },
    4: { name: "المؤسسية", price: "799 ريال", period: "شهرياً" },
  };

  const selectedPlan = planInfo[planId] || planInfo[2];

  const handleProceedToPayment = () => {
    // الانتقال لصفحة الدفع (Checkout)
    navigate("/checkout", {
      state: {
        tenant,
        planId,
        returnUrl: "/dashboard",
      },
    });
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50 py-12"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto px-4">
        {/* Header Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-6">
            <Clock className="w-12 h-12 text-yellow-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            تم إنشاء حسابك بنجاح! 🎉
          </h1>
          <p className="text-xl text-gray-600">
            خطوة واحدة أخيرة لتفعيل اشتراكك
          </p>
        </div>

        {/* Account Info Card */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">
                معلومات الحساب
              </h3>
              <div className="space-y-2 text-sm">
                <p className="text-gray-600">
                  <strong>اسم المكتب:</strong> {tenant?.name || "غير محدد"}
                </p>
                <p className="text-gray-600">
                  <strong>النطاق:</strong> {tenant?.subdomain ? `${tenant.subdomain}.lawsystem.com` : "غير محدد"}
                </p>
                <p className="text-gray-600">
                  <strong>البريد الإلكتروني:</strong> {admin?.email || "غير محدد"}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">
                  الخطة المختارة
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">
                      خطة {selectedPlan.name}
                    </span>
                    <span className="text-2xl font-bold text-blue-600">
                      {selectedPlan.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    <Calendar className="w-4 h-4 inline ml-1" />
                    الدفع {selectedPlan.period}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Status */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-yellow-900 mb-2">
                يرجى إتمام عملية الدفع لتفعيل حسابك
              </h4>
              <p className="text-yellow-800 text-sm mb-3">
                تم إنشاء حسابك بنجاح، لكنه في وضع الانتظار حتى يتم تأكيد الدفع.
                بمجرد إتمام الدفع، سيتم تفعيل حسابك فوراً ويمكنك البدء في
                استخدام جميع الميزات.
              </p>
              <div className="bg-white rounded-lg p-4 mt-4">
                <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  الدفع آمن ومضمون
                </h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>✓ تشفير SSL كامل لحماية بياناتك</li>
                  <li>✓ نظام دفع معتمد ومؤمن</li>
                  <li>✓ إمكانية الإلغاء في أي وقت</li>
                  <li>✓ ضمان استرجاع الأموال لمدة 30 يوم</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Options */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            خيارات الدفع
          </h3>

          <div className="space-y-4 mb-6">
            <div className="border-2 border-blue-200 rounded-lg p-6 bg-blue-50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      الدفع الإلكتروني
                    </h4>
                    <p className="text-sm text-gray-600">
                      بطاقة ائتمان، فيزا، ماستركارد
                    </p>
                  </div>
                </div>
                <span className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                  موصى به
                </span>
              </div>
              <button
                onClick={handleProceedToPayment}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 font-semibold"
              >
                <CreditCard className="w-5 h-5" />
                المتابعة للدفع
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <div className="border-2 border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <Mail className="w-6 h-6 text-gray-600" />
                <div>
                  <h4 className="font-semibold text-gray-900">
                    التحويل البنكي
                  </h4>
                  <p className="text-sm text-gray-600">
                    سيتم تفعيل الحساب خلال 1-2 يوم عمل
                  </p>
                </div>
              </div>
              <button
                onClick={() =>
                  navigate("/bank-transfer", { state: { tenant, planId } })
                }
                className="w-full py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition flex items-center justify-center gap-2 font-semibold"
              >
                عرض تفاصيل الحساب البنكي
              </button>
            </div>
          </div>
        </div>

        {/* Alternative Actions */}
        <div className="bg-gray-50 rounded-xl p-6 mb-8">
          <h4 className="font-semibold text-gray-900 mb-3">
            لا تريد الدفع الآن؟
          </h4>
          <p className="text-gray-600 text-sm mb-4">
            يمكنك تسجيل الدخول لاحقاً وإتمام عملية الدفع من إعدادات الحساب.
            سيبقى حسابك في وضع الانتظار حتى تأكيد الدفع.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate("/login")}
              className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition flex items-center justify-center gap-2"
            >
              تسجيل الدخول لاحقاً
            </button>
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              العودة للصفحة الرئيسية
            </button>
          </div>
        </div>

        {/* Support Info */}
        <div className="text-center text-sm text-gray-500">
          <p className="mb-2">هل تحتاج إلى مساعدة؟</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:support@lawsystem.com"
              className="text-blue-600 hover:underline"
            >
              📧 support@lawsystem.com
            </a>
            <a
              href="https://wa.me/964790123456"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:underline flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-whatsapp" viewBox="0 0 16 16">
                <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
              </svg>
              +964 790 123 456
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPending;
